import { Field } from '@base-ui/react/field';
import { Form } from '@base-ui/react/form';
import { Input } from '@base-ui/react/input';
import { useQueryClient } from '@tanstack/react-query';
import { X, Zap } from 'lucide-react';
import { type SubmitEvent, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { HoverTip } from '@/components/ui/HoverTip';
import { Modal } from '@/components/ui/Modal';
import { Separator } from '@/components/ui/Separator';
import { Spinner } from '@/components/ui/Spinner';
import { STAGES, type Stage } from '@/consts/stages';
import { cx } from '@/cva.config';
import { useToast } from '@/hooks/useToast';
import { useOverlays, useShellNav } from '@/providers/dashboard/ShellContext';

interface Result {
  company: string;
  job_id: number;
  score: number | null;
  status: string;
  title: string;
}

const STAGE_KEYS = new Set<string>(STAGES.map((s) => s.key));
STAGE_KEYS.add('idle');
STAGE_KEYS.add('error');

const isStage = (val: unknown): val is Stage =>
  typeof val === 'string' && STAGE_KEYS.has(val);

const isResult = (val: unknown): val is Result =>
  typeof val === 'object'
  && val !== null
  && 'job_id' in val
  && typeof val.job_id === 'number'
  && 'title' in val
  && typeof val.title === 'string'
  && 'company' in val
  && typeof val.company === 'string'
  && 'status' in val
  && typeof val.status === 'string';

export const AddUrlModal = () => {
  const { closeAddUrl } = useOverlays();
  const { setView } = useShellNav();
  const queryClient = useQueryClient();

  const [errors, setErrors] = useState<Record<string, string | string[]>>({});
  const [result, setResult] = useState<Result | null>(null);
  const [stage, setStage] = useState<Stage>('idle');
  const [url, setUrl] = useState('');
  const toast = useToast();

  const handleAdded = () => {
    setView('feed');
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
  };

  const processSSELine = (line: string) => {
    if (!line.startsWith('data: ')) {
      return;
    }

    let data: Record<string, unknown>;
    try {
      data = JSON.parse(line.slice(6)) as Record<string, unknown>;
    } catch {
      return; // skip malformed frames
    }

    if (isStage(data.stage)) {
      setStage(data.stage);
    }

    if (typeof data.error === 'string') {
      setErrors({ root: data.error });
      setStage('error');
    }

    if (isResult(data.result)) {
      setResult(data.result);
      setStage('done');
      toast.push(
        `Added "${data.result.title}" at ${data.result.company}`,
        'success',
      );
    }
  };

  const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!url.trim()) {
      return;
    }

    setErrors({});
    setStage('scraping');

    try {
      const res = await fetch('/api/jobs/add-url', {
        body: JSON.stringify({ url: url.trim() }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        setErrors({ root: text || `Server returned ${res.status}` });
        setStage('error');
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        throw new Error('No response stream');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          processSSELine(line);
        }
      }
    } catch {
      setErrors({ root: 'Failed to connect to server' });
      setStage('error');
    }
  };

  const stageIndex = STAGES.findIndex((s) => s.key === stage);

  return (
    <Modal onClose={closeAddUrl} open size="md">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <Modal.Title className="text-sm lg:text-base font-medium">
            Add job by URL
          </Modal.Title>
          <p className="font-mono text-2xs lg:text-xs text-subtle">
            Goblin will fetch, parse, and score it.
          </p>
        </div>
        <HoverTip align="end" placement="bottom" text="Close (Esc)">
          <Modal.Close
            aria-label="Close"
            className="text-foreground-muted transition-colors hover:text-foreground"
          >
            <X className="size-4" />
          </Modal.Close>
        </HoverTip>
      </div>

      {stage === 'idle' || stage === 'error' ? (
        <Form errors={errors} onSubmit={handleSubmit}>
          <Field.Root className="space-y-3 p-4" name="root">
            <Input
              autoFocus
              className="block h-9 w-full rounded-md border border-border bg-background px-3 font-mono text-xs lg:text-sm text-foreground transition-colors placeholder:text-subtle focus:border-accent focus:ring-2 focus:ring-accent/40 focus:outline-none"
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://linear.app/careers/senior-engineer"
              type="url"
              value={url}
            />
            <p className="font-mono text-2xs lg:text-xs text-subtle">
              Tip: works with most ATS pages (Greenhouse, Ashby, Lever,
              Workable, native pages).
            </p>
            <Field.Error className="text-xs lg:text-sm text-score-low" />
          </Field.Root>
          <Separator />
          <div className="flex items-center justify-end gap-2 bg-surface-sunken/40 px-4 py-3">
            <Button
              onClick={closeAddUrl}
              size="sm"
              type="button"
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              disabled={!url.trim()}
              size="sm"
              type="submit"
              variant="primary"
            >
              Fetch &amp; score <Zap className="size-3.5" />
            </Button>
          </div>
        </Form>
      ) : (
        <div className="space-y-3 px-5 py-4">
          {STAGES.map(({ key, label }, i) => {
            const isCurrent = key === stage;
            const isPast = stageIndex > i;
            const isDone = key === 'done' && stage === 'done';

            return (
              <div className="flex items-center gap-3" key={key}>
                <div className="flex size-6 shrink-0 items-center justify-center">
                  {isPast || isDone ? (
                    <div className="size-5 rounded-full bg-score-high/20 text-center text-xs/5 lg:text-sm/5 text-score-high">
                      &#10003;
                    </div>
                  ) : isCurrent ? (
                    <Spinner />
                  ) : (
                    <div className="size-2 rounded-full bg-border" />
                  )}
                </div>
                <span
                  className={cx('text-sm lg:text-base', {
                    'font-medium text-foreground': isCurrent,
                    'text-foreground-muted': isPast || isDone,
                    'text-foreground-muted/50':
                      !isCurrent && !isPast && !isDone,
                  })}
                >
                  {label}
                </span>
              </div>
            );
          })}

          {result && stage === 'done' && (
            <div className="mt-4 rounded-lg border border-border bg-background p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{result.title}</p>
                  <p className="text-xs lg:text-base text-foreground-muted">
                    {result.company}
                  </p>
                </div>
                {result.score !== null && (
                  <div
                    className={cx(
                      'rounded-md border px-2.5 py-1 font-mono text-xs lg:text-base font-semibold',
                      {
                        'border-score-high/30 bg-score-high/15 text-score-high':
                          result.score >= 80,
                        'border-score-low/30 bg-score-low/15 text-score-low':
                          result.score < 60,
                        'border-score-mid/30 bg-score-mid/15 text-score-mid':
                          result.score >= 60,
                      },
                    )}
                  >
                    {result.score}
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs lg:text-sm text-foreground-muted">
                Status: {result.status}
              </p>
            </div>
          )}

          {stage === 'done' && (
            <Button
              className="mt-2 py-2.5"
              fullWidth
              onClick={() => {
                handleAdded();
                closeAddUrl();
              }}
              size="lg"
            >
              View in Queue
            </Button>
          )}
        </div>
      )}
    </Modal>
  );
};
